import {
    Inject,
    ISimpleControllerConfiguration,
    ModelEvents,
    SimpleBaseController,
    SoundType,
} from '@denishussar/html5-game-engine';
import {BlurContentController} from './BlurContentController';
import {DevConfig} from './Common/DevConfig';
import {EngineEvents} from './Common/Events';
import {getSpriteFromUrl} from './Common/getSpriteFromUrl';
import {SoundConstants} from './Constants/Sounds';
import {ModalContainerController} from './Controllers/Modals/ModalContainerController';
import {GameLogic} from './Logic/GameLogic';
import {LivesLogic} from './Logic/LivesLogic';
import {GameModel, GameStateEnum} from './Models/GameModel';
import {SoundModel} from './Models/SoundModel';
import {AnalyticsController} from './Services/Analytics/AnalyticsController';
import {AdsLogic} from "./Logic/Ads/AdsLogic";
import {REWARDED_ADS_TYPE} from "./Logic/Ads/BaseAdsLogic";

export interface IRootControllerOptions {
}

export class RootController extends SimpleBaseController {
    $model: GameModel;

    private _videoSkipButtonElement: HTMLDivElement;
    private _videoElement: HTMLVideoElement;

    public constructor(
        @Inject private _options: IRootControllerOptions,
        @Inject private _analytics: AnalyticsController,
        @Inject private _gameLogic: GameLogic,
        @Inject private _livesLogic: LivesLogic,
        @Inject private _soundModel: SoundModel,
        @Inject private _modelEvents: ModelEvents,
        @Inject private _model: GameModel,
        @Inject private adsLogic: AdsLogic,
    ) {
        super('RootController');
    }

    protected getConfiguration(): ISimpleControllerConfiguration {
        this._gameLogic.initBalanceManager();
        this._gameLogic.initAnalytics();

        const events = {};
        events[EngineEvents.musicLoudness] = () => {
            this.soundManager.changeVolume(SoundConstants.YARN_IN_THE_FOREST, SoundModel.music);
        };

        return {
            modelEvents: events,
            sounds: [
                {
                    id: SoundConstants.YARN_IN_THE_FOREST,
                    type: SoundType.Music,//you can mute sfx or music separately
                },
            ],
            childControllers: {
                blurController: {
                    controller: BlurContentController,
                    model: this.$model,
                },
                modalContainer: {
                    controller: ModalContainerController,
                    options: {
                        root: this,
                    },
                    model: this.$model,
                },
            },
            modelWatch: {
                palette: () => {
                    GameModel.alphabetSymbol = GameModel.alphabetSymbolList.concat(Array.from({length: GameModel.alphabetNumberMax}, (v, k) => (k + 1).toString()));
                },
                soundOff: (state: boolean) => {
                    this.soundManager.muteSoundsByType(state, SoundType.ALL);
                },
                gameState: async (state: GameStateEnum, prevState: GameStateEnum) => {
                    if (state === GameStateEnum.main &&
                        [GameStateEnum.win, GameStateEnum.game].includes(prevState)
                    ) {
                        this.adsLogic.adsLogic.resetRewardedAdTimeout(REWARDED_ADS_TYPE.SHOP_WOOL);
                        this.adsLogic.adsLogic.resetRewardedAdTimeout(REWARDED_ADS_TYPE.SHOP_NEEDLES);
                        this.adsLogic.adsLogic.resetRewardedAdTimeout(REWARDED_ADS_TYPE.SHOP_BUCKET);
                        this.adsLogic.adsLogic.resetRewardedAdTimeout(REWARDED_ADS_TYPE.SHOP_COMBO);
                    }
                }
            },
        };
    }

    protected onInitialize() {
        setTimeout(() => {
            this.soundManager.play(SoundConstants.YARN_IN_THE_FOREST, true, SoundModel.music);
        }, 1000);

        const arr = [];
        for (let i = 1; i <= 9; i++) {
            arr.push(getSpriteFromUrl(`assets/pixel_0${i}.png`, `pixel_0${i}_img`));
        }

        Promise.all(arr)
            .then(() => {
                this._livesLogic.getLivesFromStorage();
            });
    }

    protected onLandscape(): void {
    }

    protected onPortrait(): void {
    }

    protected onUpdate(frameRate: number): void {
        if (DevConfig.LIVES_ON) {
            this._livesLogic.update(frameRate);
        }
    }

    private async playVideo(): Promise<void> {
        return new Promise(resolve => {
            this._videoElement = document.getElementById(
                'video-frame',
            ) as HTMLVideoElement;

            if (!this._videoElement) {
                this.stopVideo();
                resolve();
            } else {
                this._videoSkipButtonElement = document.getElementById(
                    'video-skip-button',
                ) as HTMLDivElement;

                this._videoSkipButtonElement.style.visibility = 'visible';
                this._videoElement.style.visibility = 'visible';

                this._videoSkipButtonElement.onclick = () => {
                    this.stopVideo();
                    resolve();
                };

                const params = 'wmode=transparent&controls=0&autoplay=1&showinfo=0&enablejsapi=1&rel=0&&playsinline=1&playlist=';
                const videoKey = 'iXxDOd96psI';

                this._videoElement.removeAttribute('allowfullscreen');
                this._videoElement.setAttribute('donotallowfullscreen', '');

                this._videoElement.src = `https://www.youtube.com/embed/${videoKey}?${params}${videoKey}`;
            }
        });
    }

    private stopVideo(): void {
        if (this._videoElement && this._videoSkipButtonElement) {
            document.body.removeChild(this._videoElement);
            document.body.removeChild(this._videoSkipButtonElement);

            this.$model.isVideoPlayed = true;

            this._videoElement = null;
            this._videoSkipButtonElement = null;
        }
    }
}
